import React from 'react';
import type { ComponentType, ClassAttributes, HTMLAttributes, ReactNode } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { styled, type StyledProps } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

// When the space-density experience is active, HubSpacingScope / hub-home-content
// set `--ak-editor-extension-block-spacing` on an ancestor. The wrapper reads
// that variable (falling back to `0`) as top/bottom PADDING on each bespoke hub
// extension, so the vertical rhythm between extensions follows the selected
// density. Padding (not margin) is used deliberately so adjacent extensions do
// not margin-collapse — the gap between two extensions is the sum of the lower
// extension's top padding and the upper extension's bottom padding. When the
// variable is unset the padding is `0`, so this is a no-op for hubs without a
// density selected.
const extensionBlockPadding = 'var(--ak-editor-extension-block-spacing, 0)';

// This is a copy of confluence/next/packages/company-hub/src/hub-layout/HubRendererWrapper.tsx
// For now we should keep them in sync until Confluence is fully migrated to hub-renderer
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-unsafe-values
export const HubRendererWrapper: ComponentType<
	ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-unsafe-values
> = styled.div({
	// removing 96 gutter (akEditorBreakoutPadding) width by appling negative margin
	// this increases the width of extension by 96px
	// we have to adjust this value
	// in Editor we adds 40px in the same way
	// to compensate the difference we limit the width of extension to
	// 704px - default (800 - 96)
	// 955px - wide (1051 - 96)
	// 1744px - full width (1840 - 96)
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& .ak-renderer-document, & [data-multibodiedextension-container]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div.ak-renderer-extension': {
			// Horizontal gutter (unchanged). Vertical padding follows the selected
			// space density (0 when unset — a no-op for hubs without a density).
			paddingTop: extensionBlockPadding,
			paddingBottom: extensionBlockPadding,
			paddingLeft: token('space.600', '48px'),
			paddingRight: token('space.600', '48px'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& > div.ak-renderer-extension-overflow-container': {
				margin: '0 -48px',
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div.ak-renderer-extension[data-layout=default]': {
			// extensions with default layout should have 800px max-width
			// for screens larger than 736px we limit the max-width to 704px -> 704+48*2 = 800
			// for screens smaller than 736px we limit the max-width to 100% - 32px -> 100% - 32 + 48*2 = 100% + 64(page padding) -> full width
			maxWidth: 'min(704px, calc(100% - 32px))',
			// positioning extension with default layout in the center
			// in the same way as we do for wide and full-width extensions
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginLeft: '50%',
			transform: 'translateX(-50%)',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div.ak-renderer-extension[data-layout=wide]': {
			maxWidth: '955px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div.ak-renderer-extension[data-layout=full-width]': {
			maxWidth: '1744px',
		},
	},

	// Removing the margin between MBEs (Sections and any other future MBE extensions)
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-multiBodiedExtension-container] + [data-multiBodiedExtension-container]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > .ak-renderer-extension': {
			marginTop: 0,
		},
	},

	// Removing the margin between MBE and any extension
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-multiBodiedExtension-container] + div.ak-renderer-extension': {
		marginTop: 0,
	},

	// Removing the margin between any extension and MBE
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div.ak-renderer-extension + [data-multiBodiedExtension-container]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > .ak-renderer-extension': {
			marginTop: 0,
		},
	},

	// Removing the margin between any extensions
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div.ak-renderer-extension + div.ak-renderer-extension': {
		marginTop: 0,
	},
});

const HubRendererWrapperWithPadding: ComponentType<
	ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/ui-styling-standard/no-unsafe-values
> = styled.div({
	paddingInlineStart: token('space.300'),
	paddingInlineEnd: token('space.300'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& .ak-renderer-document, & [data-multibodiedextension-container]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div.ak-renderer-extension': {
			// Horizontal gutter (unchanged). Vertical padding follows the selected
			// space density (0 when unset — a no-op for hubs without a density).
			paddingTop: extensionBlockPadding,
			paddingBottom: extensionBlockPadding,
			paddingLeft: token('space.600', '48px'),
			paddingRight: token('space.600', '48px'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'& > div.ak-renderer-extension-overflow-container': {
				margin: '0 -48px',
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div.ak-renderer-extension[data-layout=default]': {
			maxWidth: 'min(704px, calc(100% - 32px))',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/use-tokens-space
			marginLeft: '50%',
			transform: 'translateX(-50%)',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div.ak-renderer-extension[data-layout=wide]': {
			maxWidth: '955px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div.ak-renderer-extension[data-layout=full-width]': {
			maxWidth: '1744px',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-multiBodiedExtension-container] + [data-multiBodiedExtension-container]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > .ak-renderer-extension': {
			marginTop: 0,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-multiBodiedExtension-container] + div.ak-renderer-extension': {
		marginTop: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div.ak-renderer-extension + [data-multiBodiedExtension-container]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > .ak-renderer-extension': {
			marginTop: 0,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'div.ak-renderer-extension + div.ak-renderer-extension': {
		marginTop: 0,
	},
});

type HubProduct = 'JSM' | 'CSM';

type HubRendererWrapperGatedProps = ClassAttributes<HTMLDivElement> &
	HTMLAttributes<HTMLDivElement> & { children?: ReactNode; product?: HubProduct };

export const HubRendererWrapperGated = ({
	children,
	product,
	...props
}: HubRendererWrapperGatedProps): React.JSX.Element => {
	if (product === 'JSM' && fg('hubs_help_seeker_horizontal_padding')) {
		return <HubRendererWrapperWithPadding {...props}>{children}</HubRendererWrapperWithPadding>;
	}
	return <HubRendererWrapper {...props}>{children}</HubRendererWrapper>;
};
